import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Grid,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import { StageProcessor } from '../components/StageProcessor';
import { searchVendors } from '../api/stageFunctions';
import { ProductWithCodes } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface ProductWithVendors extends ProductWithCodes {
  vendors: string[];  // Array of vendor names from the script
}

interface VendorDiscoveryProps {
  products: ProductWithCodes[];
  onComplete: (productsWithVendors: ProductWithVendors[]) => void;
  flowId?: string | null;
}

const VendorDiscovery: React.FC<VendorDiscoveryProps> = ({
  products,
  onComplete,
  flowId,
}) => {
  const [productsWithVendors, setProductsWithVendors] = useState<ProductWithVendors[]>([]);
  const [triggerSearch, setTriggerSearch] = useState(0);
  const [useCachedData, setUseCachedData] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Check cache for this stage
  const { cachedData, isCacheValid, isCheckingCache, refreshCache, checkCacheManually, validateCacheWithInput } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.VENDORS,
    inputData: { products },
    enabled: useCachedData,
  });
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Validate cache only once per distinct input (flowId + products snapshot)
  const validatedOnceRef = useRef<boolean>(false);
  useEffect(() => {
    if (flowId && useCachedData && !validatedOnceRef.current) {
      validatedOnceRef.current = true;
      validateCacheWithInput();
    }
  }, [flowId, useCachedData, validateCacheWithInput]);

  // Default checkbox to match cache availability (disabled when no cache)
  useEffect(() => {
    if (!isCheckingCache) {
      setUseCachedData(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Auto-search on mount - always trigger at least once
  useEffect(() => {
    // Wait for cache check to complete
    if (!isCheckingCache && !hasInitialized) {
      setHasInitialized(true);
      // Always trigger at least once to let StageProcessor handle cached or fresh data
      handleSearchVendors();
    }
  }, [isCheckingCache, hasInitialized]);
  
  // Load cached data when available
  useEffect(() => {
    if (isCacheValid && cachedData && useCachedData && productsWithVendors.length === 0) {
      handleVendorsFound(cachedData);
    }
  }, [isCacheValid, cachedData, useCachedData, productsWithVendors.length]);

  const handleSearchVendors = () => {
    setTriggerSearch(prev => prev + 1);
  };

  const handleVendorsFound = (data: any) => {
    // With UI-ready format, products are consistently formatted
    const products = data.productsWithVendors || data.products || data;
    
    if (!Array.isArray(products)) {
      console.error('VendorDiscovery: Expected products array but got:', data);
      return;
    }
    
    // UI-ready format already has all required fields - no transformation needed
    setProductsWithVendors(products);
    
    // Auto-expand products that have vendors
    const toExpand = new Set(
      products.filter(p => p.vendors && p.vendors.length > 0).map(p => p.id)
    );
    setExpandedProducts(toExpand);
  };

  const handleComplete = () => {
    onComplete(productsWithVendors);
  };

  const toggleProductExpansion = (productId: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  // Calculate statistics
  const stats = {
    totalProducts: productsWithVendors.length,
    productsWithVendors: productsWithVendors.filter(p => p.vendors && p.vendors.length > 0).length,
    totalVendors: productsWithVendors.reduce((sum, p) => sum + (p.vendors?.length || 0), 0),
    uniqueVendors: new Set(productsWithVendors.flatMap(p => p.vendors || [])).size,
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <StorefrontIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Vendor Discovery
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Finding qualified vendors from USAspending.gov based on NAICS codes. 
          These vendors have past performance in the relevant industry classifications.
        </Typography>
      </Paper>

      {/* Process Vendor Search */}
      {/* Cache Control and Search Button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCachedData}
                onChange={(e) => setUseCachedData(e.target.checked)}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<StorefrontIcon />}
            onClick={() => setTriggerSearch(prev => prev + 1)}
            disabled={isCheckingCache}
          >
            {getButtonText('Find Vendors', useCachedData, cachedData && cachedData.products_with_vendors)}
          </Button>
        </Box>
      </Paper>
      
      <StageProcessor
        title="Searching for Vendors"
        description="Querying USAspending API for vendors with experience in the identified NAICS codes..."
        processFunction={() => searchVendors(products, flowId, !useCachedData)}
        onSuccess={handleVendorsFound}
        trigger={triggerSearch}
        autoProcess={false}
        showStatus={true}
      >
        {(data, status) => {
          if (!data || productsWithVendors.length === 0) return null;

          return (
            <>
              {/* Statistics */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Discovery Summary:
                  </Typography>
                  <Chip 
                    label={`${stats.totalProducts} Products`} 
                    color="primary" 
                  />
                  <Chip 
                    label={`${stats.productsWithVendors} with Vendors`} 
                    color="success"
                  />
                  <Chip 
                    label={`${stats.uniqueVendors} Unique Vendors`} 
                    color="info"
                  />
                  <Chip 
                    label={`${stats.totalVendors} Total Assignments`} 
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Paper>

              {/* Info Alert */}
              <Alert severity="info" sx={{ mb: 2 }}>
                The vendors shown below have been identified through USAspending.gov based on their 
                past contracts with the relevant NAICS codes. Review each product's vendor list 
                to identify potential suppliers.
              </Alert>

              {/* Products with Vendors */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Products and Their Potential Vendors
                </Typography>
                
                {productsWithVendors.map((product) => (
                  <Accordion 
                    key={product.id}
                    expanded={expandedProducts.has(product.id)}
                    onChange={() => toggleProductExpansion(product.id)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {product.product_name || product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {product.manufacturer} {product.model && `- ${product.model}`}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1} alignItems="center">
                          {/* Show PSC and NAICS codes with tooltips */}
                          {product.psc_codes && product.psc_codes.length > 0 && (
                            (() => {
                              const pscData = product.psc_codes[0];
                              const isObject = typeof pscData === 'object' && pscData !== null;
                              let code = isObject ? pscData.code : String(pscData);
                              let description = isObject ? pscData.description : '';
                              
                              // Handle case where code contains description (e.g., "4240: Safety...")
                              if (code.includes(':')) {
                                const parts = code.split(':');
                                code = parts[0].trim();
                                if (!description && parts[1]) {
                                  description = parts.slice(1).join(':').trim();
                                }
                              }
                              
                              const chip = (
                                <Chip 
                                  label={`PSC: ${code}`} 
                                  size="small" 
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ cursor: description ? 'help' : 'default' }}
                                />
                              );
                              
                              // Wrap in tooltip if we have a description
                              if (description) {
                                return (
                                  <Tooltip title={description} placement="top" arrow>
                                    {chip}
                                  </Tooltip>
                                );
                              }
                              return chip;
                            })()
                          )}
                          {product.naics_from_psc && product.naics_from_psc.length > 0 && (
                            (() => {
                              const naicsCode = product.naics_from_psc[0];
                              // Check if we have NAICS descriptions
                              const naicsWithDesc = product.naics_with_descriptions || [];
                              const naicsDesc = naicsWithDesc.find((item: any) => item.code === naicsCode);
                              const description = naicsDesc?.description || '';
                              
                              const chip = (
                                <Chip 
                                  label={`NAICS: ${naicsCode}`} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                  sx={{ cursor: description ? 'help' : 'default' }}
                                />
                              );
                              
                              // Wrap in tooltip if we have a description
                              if (description) {
                                return (
                                  <Tooltip title={description} placement="top" arrow>
                                    {chip}
                                  </Tooltip>
                                );
                              }
                              return chip;
                            })()
                          )}
                          <Chip 
                            label={`${product.vendors?.length || 0} vendors`} 
                            color={product.vendors?.length > 0 ? "success" : "default"}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Product Details */}
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Product Information
                          </Typography>
                          {product.specifications?.award_amount && (
                            <Typography variant="body2">
                              <strong>Contract Value:</strong> {product.specifications.award_amount}
                            </Typography>
                          )}
                          {product.evidence?.source && (
                            <Typography variant="body2">
                              <strong>Source:</strong>{' '}
                              {product.evidence?.url ? (
                                <a 
                                  href={product.evidence.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#1976d2', textDecoration: 'none' }}
                                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                >
                                  {product.evidence.source}
                                </a>
                              ) : (
                                product.evidence.source
                              )}
                            </Typography>
                          )}
                          {product.naics_from_psc && product.naics_from_psc.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>All NAICS Codes:</strong>
                              </Typography>
                              <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                {product.naics_from_psc.map((code, idx) => {
                                  // Check if we have NAICS descriptions
                                  const naicsWithDesc = product.naics_with_descriptions || [];
                                  const naicsDesc = naicsWithDesc.find((item: any) => item.code === code);
                                  const description = naicsDesc?.description || '';
                                  
                                  const chip = (
                                    <Chip 
                                      key={idx}
                                      label={code} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{ cursor: description ? 'help' : 'default' }}
                                    />
                                  );
                                  
                                  // Wrap in tooltip if we have a description
                                  if (description) {
                                    return (
                                      <Tooltip key={idx} title={description} placement="top" arrow>
                                        {chip}
                                      </Tooltip>
                                    );
                                  }
                                  return chip;
                                })}
                              </Box>
                            </Box>
                          )}
                        </Grid>

                        {/* Vendor List */}
                        <Grid item xs={12} md={8}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Potential Vendors ({product.vendors?.length || 0})
                          </Typography>
                          {product.vendors && product.vendors.length > 0 ? (
                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                              <List dense>
                                {product.vendors.map((vendorName, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <BusinessIcon fontSize="small" color="action" />
                                          <Typography variant="body2">
                                            {vendorName}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              No vendors found for this product's NAICS codes. 
                              Consider expanding the search or checking alternative codes.
                            </Alert>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Review the vendor lists and proceed to generate procurement documents
                  </Typography>
                  <Box>
                    {/* JSON export removed for non-technical users */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleComplete}
                      disabled={stats.productsWithVendors === 0}
                    >
                      Generate Section B
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          );
        }}
      </StageProcessor>
    </Box>
  );
};

export default VendorDiscovery;
